import { useMemo, useState } from "react";
import Editor from "../../../UI/Form/Editor/Editor";
import styles from "./AdminShared.module.css";
import {
  getModelFieldKeys,
  getFieldKind,
  getRequiredFieldsForItem,
  isSystemField,
  toTitleCase,
  isTruthyFlag,
  normalizeSourceURLObj,
  SERVICE_SPECIAL_FIELDS,
  isFieldVisibleForItemType,
  sortFieldsForDisplay,
  DEFAULT_CATALOG_TYPES,
  DEFAULT_CONTENT_TYPES,
  getItemTypeThemeKey,
} from "./adminUtils";
import {
  DEFAULT_PRE_RELEASE_MESSAGE,
  formatReleaseCountdown,
  formatReleaseFullDate,
  getEffectivePreReleaseMessage,
  isUpcomingRelease,
} from "../../../Hooks/catalogPresentation";
import {
  extractIframeAttributesFromEmbed,
  joinIframeAttributeSections,
  splitIframeAttributeSections,
} from "../../../Hooks/parseIframeCustomAttributes";
import {
  extractDefaultQueryFromEmbed,
  extractEmbedSrc,
} from "../../../Hooks/embedUrlQuery";
import {
  CONNECT_SPLASH_BOOLEAN_FIELDS,
  CONNECT_SPLASH_DEFAULTS,
  CONNECT_SPLASH_FIELD_KEYS,
  CONNECT_SPLASH_FIELD_META,
  CONNECT_SPLASH_GROUPS,
  isConnectSplashType,
} from "../../../data/connectSplashConfig";
import {
  JOIN_INVITE_BOOLEAN_FIELDS,
  JOIN_INVITE_DEFAULTS,
  JOIN_INVITE_FIELD_KEYS,
  JOIN_INVITE_FIELD_META,
  JOIN_INVITE_GROUPS,
  JOIN_INVITE_NUMBER_FIELDS,
  JOIN_INVITE_URL_FIELDS,
  isJoinInviteType,
} from "../../../data/joinInviteConfig";

const CMS_EXTRA_FORMS = [
  {
    match: isConnectSplashType,
    groups: CONNECT_SPLASH_GROUPS,
    keys: CONNECT_SPLASH_FIELD_KEYS,
    meta: CONNECT_SPLASH_FIELD_META,
    defaults: CONNECT_SPLASH_DEFAULTS,
    booleans: CONNECT_SPLASH_BOOLEAN_FIELDS,
    numberFields: {
      confirmedAutoSeconds: { min: 0, max: 120, step: 1 },
    },
    urlFields: [],
    theme: "connectsplash",
    typeHint:
      "One active Connect Splash item drives both Join screens. Blank text fields keep the site defaults. Save draft to keep this off the live site. Preview to see it on the real homepage.",
  },
  {
    match: isJoinInviteType,
    groups: JOIN_INVITE_GROUPS,
    keys: JOIN_INVITE_FIELD_KEYS,
    meta: JOIN_INVITE_FIELD_META,
    defaults: JOIN_INVITE_DEFAULTS,
    booleans: JOIN_INVITE_BOOLEAN_FIELDS,
    numberFields: JOIN_INVITE_NUMBER_FIELDS,
    urlFields: JOIN_INVITE_URL_FIELDS,
    theme: "joininvite",
    typeHint:
      "One active Join Invite item drives the homepage email popup. Uncheck Show the invite modal to hide it. Header, page, and footer Join stay available. Save draft to keep this off the live site. Preview to see it on the real homepage.",
  },
];

/**
 * Schema-driven field form for create/edit.
 * values: plain object; onChange(field, value); errors: { field: msg }
 */
const AdminFieldForm = ({
  model,
  values,
  onChange,
  errors = {},
  services = [],
  section = "catalog",
  showSystem = false,
  onToggleSystem,
  mode = "edit",
  typeOptions = null,
}) => {
  const itemType = values?.type != null ? String(values.type).trim() : "";

  const required = useMemo(
    () => getRequiredFieldsForItem(model, itemType, section),
    [model, itemType, section],
  );

  const typeSelectOptions = useMemo(() => {
    const defaults =
      section === "content" ? DEFAULT_CONTENT_TYPES : DEFAULT_CATALOG_TYPES;
    const set = new Set([
      ...(typeOptions || []),
      ...defaults,
      ...(itemType ? [itemType] : []),
    ]);
    return Array.from(set)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [typeOptions, section, itemType]);

  const fieldKeys = useMemo(() => {
    const all = getModelFieldKeys(model, { section });
    const visible = all.filter((k) => {
      if (isSystemField(k)) return true; // system section filters separately
      return isFieldVisibleForItemType(k, itemType, section);
    });
    return sortFieldsForDisplay(visible);
  }, [model, section, itemType]);

  const mainFields = fieldKeys.filter(
    (k) => !isSystemField(k) && !SERVICE_SPECIAL_FIELDS.includes(k),
  );
  const systemFields = fieldKeys.filter((k) => isSystemField(k));
  const extraFields = fieldKeys.filter(
    (k) => SERVICE_SPECIAL_FIELDS.includes(k) && !isSystemField(k),
  );
  const extraForm = CMS_EXTRA_FORMS.find((form) => form.match(itemType));
  const extraKeySet = extraForm ? new Set(extraForm.keys) : null;
  const genericMainFields = extraKeySet
    ? mainFields.filter((k) => !extraKeySet.has(k))
    : mainFields;

  const [embedPasteDraft, setEmbedPasteDraft] = useState("");
  const [embedPasteNote, setEmbedPasteNote] = useState("");
  /** Paste draft for Full-size player iframe attribute extraction */
  const [iframeAttrsPasteDraft, setIframeAttrsPasteDraft] = useState("");
  const [iframeAttrsPasteNote, setIframeAttrsPasteNote] = useState("");
  /** Per-service paste drafts for song/album streaming URL rows */
  const [sourceEmbedPaste, setSourceEmbedPaste] = useState({});
  const [sourceEmbedNote, setSourceEmbedNote] = useState({});

  const renderField = (key) => {
    const kind = getFieldKind(key);
    const requiredField = required.includes(key);
    const value = values?.[key];
    const error = errors?.[key];

    // Type drives which other fields are shown — use a select of known types
    if (key === "type") {
      const typeKey = getItemTypeThemeKey(value);
      return (
        <div
          key={key}
          className={`${styles.field} ${requiredField ? styles.required : ""} ${
            typeKey
              ? `${styles["item-type-surface"]} ${styles[`item-type-${typeKey}`]}`
              : ""
          }`}
        >
          <label htmlFor={`field-${key}`}>Type</label>
          <p className={styles["field-hint"]}>
            {section === "catalog"
              ? "Song, album, and playlist share music fields. Service is for streaming platforms (embed settings)."
              : "Chooses which content fields apply (notices, about, social links, menu order, join splash, join invite)."}
          </p>
          {CMS_EXTRA_FORMS.find((form) => form.match(value))?.typeHint && (
            <p className={styles["field-hint"]}>
              {CMS_EXTRA_FORMS.find((form) => form.match(value)).typeHint}
            </p>
          )}
          <div className={styles["type-field-row"]}>
            <select
              id={`field-${key}`}
              className={styles["select-input"]}
              value={value || ""}
              onChange={(e) => onChange(key, e.target.value)}
            >
              {!value && (
                <option value="" disabled>
                  Select a type…
                </option>
              )}
              {typeSelectOptions.map((t) => (
                <option key={t} value={t}>
                  {toTitleCase(t)}
                </option>
              ))}
            </select>
            {typeKey ? (
              <span
                className={`${styles["badge-item-type"]} ${styles[`item-type-${typeKey}`]}`}
              >
                {toTitleCase(typeKey)}
              </span>
            ) : null}
          </div>
          {error && <p className={styles["field-error"]}>{error}</p>}
        </div>
      );
    }

    // Service-level default embed query (overlay on every item URL for this service)
    if (key === "defaultEmbedQuery") {
      const applyPaste = () => {
        const extracted = extractDefaultQueryFromEmbed(embedPasteDraft);
        if (!extracted) {
          setEmbedPasteNote(
            "Could not find query parameters. Paste a full iframe or embed URL.",
          );
          return;
        }
        onChange(key, extracted);
        setEmbedPasteNote(
          "Extracted preference params (resource identity like SoundCloud url= is omitted).",
        );
        setEmbedPasteDraft("");
      };
      const pasteNoteIsError =
        embedPasteNote && /^could not/i.test(embedPasteNote);
      return (
        <div
          key={key}
          className={`${styles.field} ${styles["span-full"]} ${
            requiredField ? styles.required : ""
          }`}
        >
          <label htmlFor="field-defaultEmbedQuery">
            Default embed URL parameters
          </label>
          <p className={styles["field-hint"]}>
            For <strong>type = service</strong> only. Preference query params
            applied to every song/album/playlist embed for this service (e.g.{" "}
            <code>show_teaser=false&amp;color=%23070d07</code>). Service values
            override the same keys on the item URL; identity params (like
            SoundCloud&apos;s nested <code>url=</code>) stay on the item.
          </p>

          <div className={styles["field-with-tool"]}>
            <div className={styles["field-target-block"]}>
              <textarea
                id="field-defaultEmbedQuery"
                className={styles["textarea-input"]}
                value={value == null ? "" : String(value)}
                onChange={(e) => onChange(key, e.target.value)}
                rows={3}
                placeholder="show_teaser=false&color=%23070d07"
              />
            </div>

            <div
              className={styles["field-tool"]}
              role="group"
              aria-label="Paste embed to fill Default embed URL parameters"
            >
              <div className={styles["field-tool-header"]}>
                <span className={styles["field-tool-badge"]}>Tool</span>
                <p className={styles["field-tool-title"]}>
                  Paste full embed → URL parameters
                </p>
              </div>
              <p className={styles["field-tool-feeds"]}>
                <span className={styles["field-tool-feeds-arrow"]} aria-hidden="true">
                  ↑
                </span>
                Fills{" "}
                <strong>Default embed URL parameters</strong> above
              </p>
              <p className={styles["field-hint"]}>
                Paste a SoundCloud / Spotify / etc. iframe snippet or full{" "}
                <code>src</code> URL. Pulls preference query params only (not
                track identity like SoundCloud <code>url=</code>).
              </p>
              <label
                htmlFor="field-defaultEmbedQuery-paste"
                className={styles["visually-hidden"]}
              >
                Embed paste for URL parameters
              </label>
              <textarea
                id="field-defaultEmbedQuery-paste"
                className={styles["textarea-input"]}
                value={embedPasteDraft}
                onChange={(e) => {
                  setEmbedPasteDraft(e.target.value);
                  setEmbedPasteNote("");
                }}
                rows={3}
                placeholder='<iframe src="https://w.soundcloud.com/player/?url=…&show_teaser=true&visual=true" …>'
              />
              <div className={styles["field-tool-actions"]}>
                <button
                  type="button"
                  className={styles["btn-tool"]}
                  onClick={applyPaste}
                  disabled={!embedPasteDraft.trim()}
                >
                  Extract into URL parameters field
                </button>
              </div>
              {embedPasteNote && (
                <p
                  className={`${styles["field-tool-note"]} ${
                    pasteNoteIsError
                      ? styles["field-tool-note-err"]
                      : styles["field-tool-note-ok"]
                  }`}
                  role="status"
                >
                  {embedPasteNote}
                </p>
              )}
            </div>
          </div>

          {error && <p className={styles["field-error"]}>{error}</p>}
        </div>
      );
    }

    // Dual UI for service iframe attrs — still one DB string (full + optional small)
    if (key === "iframeCustomAttributes") {
      const { full, small } = splitIframeAttributeSections(
        value == null ? "" : String(value),
      );
      const commitSections = (nextFull, nextSmall) => {
        onChange(
          key,
          joinIframeAttributeSections({ full: nextFull, small: nextSmall }),
        );
      };
      const applyIframeAttrsPaste = () => {
        const extracted = extractIframeAttributesFromEmbed(iframeAttrsPasteDraft);
        if (!extracted) {
          setIframeAttrsPasteNote(
            "Could not find iframe attributes. Paste a full iframe embed snippet.",
          );
          return;
        }
        commitSections(extracted, small);
        setIframeAttrsPasteNote(
          "Extracted iframe parameters into Full-size player (src omitted — use Streaming URLs / Default embed URL parameters for that).",
        );
        setIframeAttrsPasteDraft("");
      };
      const iframePasteNoteIsError =
        iframeAttrsPasteNote && /^could not/i.test(iframeAttrsPasteNote);
      return (
        <div
          key={key}
          className={`${styles.field} ${styles["span-full"]} ${
            requiredField ? styles.required : ""
          }`}
        >
          <label>Iframe custom attributes</label>
          <p className={styles["field-hint"]}>
            For <strong>type = service</strong> only. Stored as one field; use
            HTML-like attributes (quotes optional). Most players use{" "}
            <strong>Full-size</strong>. <strong>Small / compact</strong> applies
            only to <strong>non-featured songs</strong> in the catalog browse
            grid — never to albums, playlists, featured items, or the home
            player.
          </p>

          <label
            htmlFor="field-iframe-attrs-full"
            className={styles["field-sublabel"]}
            style={{ marginTop: "0.65rem" }}
          >
            Full-size player (default)
          </label>
          <p className={styles["field-hint"]}>
            Used for albums, playlists, featured songs, the home welcome
            player, and any song that does not use the small section below.
            Example:{" "}
            <code>
              height=&quot;352&quot; scrolling=&quot;no&quot;
              styles=&quot;marginBottom: 0px&quot;
            </code>
          </p>

          <div className={styles["field-with-tool"]}>
            <div className={styles["field-target-block"]}>
              <textarea
                id="field-iframe-attrs-full"
                className={styles["textarea-input"]}
                value={full}
                onChange={(e) => commitSections(e.target.value, small)}
                rows={4}
                placeholder='height="352" scrolling="no" frameborder="no"'
              />
            </div>

            <div
              className={styles["field-tool"]}
              role="group"
              aria-label="Paste embed to fill Full-size player attributes"
            >
              <div className={styles["field-tool-header"]}>
                <span className={styles["field-tool-badge"]}>Tool</span>
                <p className={styles["field-tool-title"]}>
                  Paste full embed → iframe parameters
                </p>
              </div>
              <p className={styles["field-tool-feeds"]}>
                <span className={styles["field-tool-feeds-arrow"]} aria-hidden="true">
                  ↑
                </span>
                Fills <strong>Full-size player (default)</strong> above
              </p>
              <p className={styles["field-hint"]}>
                Paste a SoundCloud / Spotify / etc. <code>&lt;iframe&gt;</code>{" "}
                snippet. Pulls player chrome (
                <code>height</code>, <code>scrolling</code>,{" "}
                <code>frameborder</code>, <code>allow</code>,{" "}
                <code>style</code> → <code>styles</code>, …) and skips{" "}
                <code>src</code>.
              </p>
              <label
                htmlFor="field-iframe-attrs-paste"
                className={styles["visually-hidden"]}
              >
                Embed paste for Full-size iframe attributes
              </label>
              <textarea
                id="field-iframe-attrs-paste"
                className={styles["textarea-input"]}
                value={iframeAttrsPasteDraft}
                onChange={(e) => {
                  setIframeAttrsPasteDraft(e.target.value);
                  setIframeAttrsPasteNote("");
                }}
                rows={3}
                placeholder='<iframe width="100%" height="352" scrolling="no" frameborder="no" allow="autoplay" src="https://…"></iframe>'
              />
              <div className={styles["field-tool-actions"]}>
                <button
                  type="button"
                  className={styles["btn-tool"]}
                  onClick={applyIframeAttrsPaste}
                  disabled={!iframeAttrsPasteDraft.trim()}
                >
                  Extract into Full-size field
                </button>
              </div>
              {iframeAttrsPasteNote && (
                <p
                  className={`${styles["field-tool-note"]} ${
                    iframePasteNoteIsError
                      ? styles["field-tool-note-err"]
                      : styles["field-tool-note-ok"]
                  }`}
                  role="status"
                >
                  {iframeAttrsPasteNote}
                </p>
              )}
            </div>
          </div>

          <label
            htmlFor="field-iframe-attrs-small"
            className={styles["field-sublabel"]}
            style={{ marginTop: "1rem" }}
          >
            Small / compact player — non-featured songs only (optional)
          </label>
          <p className={styles["field-hint"]}>
            <strong>Songs only</strong>, and only when they appear in the
            catalog browse grid as <strong>not featured</strong>. Does{" "}
            <strong>not</strong> apply to albums or playlists (those always use
            full-size above), featured items, or the home player. Leave blank to
            reuse full-size settings for those songs too. Example:{" "}
            <code>height=&quot;175&quot; scrolling=&quot;no&quot;</code>
          </p>
          <textarea
            id="field-iframe-attrs-small"
            className={styles["textarea-input"]}
            value={small}
            onChange={(e) => commitSections(full, e.target.value)}
            rows={3}
            placeholder='height="175" scrolling="no" — non-featured songs only'
          />

          {error && <p className={styles["field-error"]}>{error}</p>}
        </div>
      );
    }

    if (kind === "boolean") {
      return (
        <div
          key={key}
          className={`${styles.field} ${requiredField ? styles.required : ""}`}
        >
          <label className={styles["checkbox-row"]} htmlFor={`field-${key}`}>
            <input
              id={`field-${key}`}
              type="checkbox"
              checked={isTruthyFlag(value)}
              onChange={(e) =>
                onChange(key, e.target.checked ? "true" : "false")
              }
            />
            <span>{toTitleCase(key)}</span>
          </label>
          {key === "active" && (
            <p className={styles["field-hint"]}>
              Active shows or hides the published copy on the public site.
              Publish writes that copy; it does not turn Active on or off.
            </p>
          )}
          {error && <p className={styles["field-error"]}>{error}</p>}
        </div>
      );
    }

    if (kind === "sourceURLObj") {
      const urlObj = normalizeSourceURLObj(value);
      const serviceList =
        services.filter(Boolean).length > 0
          ? services.filter(Boolean)
          : Object.keys(urlObj);

      const applySourceEmbedPaste = (serviceName) => {
        const draft = sourceEmbedPaste[serviceName] || "";
        const src = extractEmbedSrc(draft);
        if (!src) {
          setSourceEmbedNote((prev) => ({
            ...prev,
            [serviceName]:
              "Could not find an embed src. Paste a full iframe or a bare https URL.",
          }));
          return;
        }
        onChange("sourceURLObj", {
          ...urlObj,
          [serviceName]: src,
        });
        setSourceEmbedPaste((prev) => ({ ...prev, [serviceName]: "" }));
        setSourceEmbedNote((prev) => ({
          ...prev,
          [serviceName]: "Extracted embed src into the URL field above.",
        }));
      };

      return (
        <div
          key={key}
          className={`${styles.field} ${styles["span-full"]} ${
            requiredField ? styles.required : ""
          }`}
        >
          <label>Streaming URLs</label>
          <p className={styles["field-hint"]}>
            One embed <code>src</code> per service (song / album / playlist).
            Type or paste a full <code>&lt;iframe&gt;</code> into each
            service&apos;s <strong>Tool</strong> — we fill the URL field with
            the <code>src</code> only (including track identity). Service-level{" "}
            <strong>Default embed URL parameters</strong> merge on top when
            playing. Empty services are omitted on save when possible.
          </p>
          <div className={styles["service-url-grid"]}>
            {serviceList.length === 0 && (
              <p className={styles["field-hint"]}>
                No services defined yet. Add catalog items with type{" "}
                <code>service</code> first.
              </p>
            )}
            {serviceList.map((serviceName) => {
              const note = sourceEmbedNote[serviceName] || "";
              const noteIsError = note && /^could not/i.test(note);
              return (
                <div key={serviceName} className={styles["service-url-block"]}>
                  <p className={styles["service-url-heading"]}>{serviceName}</p>
                  <div className={styles["field-with-tool"]}>
                    <div className={styles["field-target-block"]}>
                      <label
                        htmlFor={`url-${serviceName}`}
                        className={styles["field-sublabel"]}
                      >
                        {serviceName} embed URL
                      </label>
                      <input
                        id={`url-${serviceName}`}
                        type="url"
                        className={styles["text-input"]}
                        value={urlObj[serviceName] || ""}
                        placeholder={`https://… (${serviceName})`}
                        onChange={(e) =>
                          onChange("sourceURLObj", {
                            ...urlObj,
                            [serviceName]: e.target.value,
                          })
                        }
                        onPaste={(e) => {
                          const text = e.clipboardData?.getData("text") || "";
                          if (!text || !/iframe|src\s*=/i.test(text)) return;
                          const src = extractEmbedSrc(text);
                          if (!src) return;
                          e.preventDefault();
                          onChange("sourceURLObj", {
                            ...urlObj,
                            [serviceName]: src,
                          });
                          setSourceEmbedNote((prev) => ({
                            ...prev,
                            [serviceName]:
                              "Extracted embed src from paste into the URL field.",
                          }));
                        }}
                      />
                    </div>

                    <div
                      className={styles["field-tool"]}
                      role="group"
                      aria-label={`Paste embed to fill ${serviceName} URL`}
                    >
                      <div className={styles["field-tool-header"]}>
                        <span className={styles["field-tool-badge"]}>Tool</span>
                        <p className={styles["field-tool-title"]}>
                          Paste full embed → {serviceName} URL
                        </p>
                      </div>
                      <p className={styles["field-tool-feeds"]}>
                        <span
                          className={styles["field-tool-feeds-arrow"]}
                          aria-hidden="true"
                        >
                          ↑
                        </span>
                        Fills <strong>{serviceName} embed URL</strong> above
                      </p>
                      <p className={styles["field-hint"]}>
                        Paste this service&apos;s full{" "}
                        <code>&lt;iframe&gt;</code> snippet or embed URL. We
                        keep the <code>src</code> only (including track
                        identity). Preference params are not stored here — they
                        come from the service&apos;s{" "}
                        <strong>Default embed URL parameters</strong> at play
                        time. You can also paste an iframe directly into the URL
                        field above.
                      </p>
                      <label
                        htmlFor={`url-paste-${serviceName}`}
                        className={styles["visually-hidden"]}
                      >
                        Embed paste for {serviceName} URL
                      </label>
                      <textarea
                        id={`url-paste-${serviceName}`}
                        className={styles["textarea-input"]}
                        value={sourceEmbedPaste[serviceName] || ""}
                        onChange={(e) => {
                          const next = e.target.value;
                          setSourceEmbedPaste((prev) => ({
                            ...prev,
                            [serviceName]: next,
                          }));
                          setSourceEmbedNote((prev) => ({
                            ...prev,
                            [serviceName]: "",
                          }));
                        }}
                        rows={2}
                        placeholder={`<iframe src="https://…" …></iframe>`}
                      />
                      <div className={styles["field-tool-actions"]}>
                        <button
                          type="button"
                          className={styles["btn-tool"]}
                          onClick={() => applySourceEmbedPaste(serviceName)}
                          disabled={
                            !(sourceEmbedPaste[serviceName] || "").trim()
                          }
                        >
                          Extract src into URL field
                        </button>
                      </div>
                      {note ? (
                        <p
                          className={`${styles["field-tool-note"]} ${
                            noteIsError
                              ? styles["field-tool-note-err"]
                              : styles["field-tool-note-ok"]
                          }`}
                          role="status"
                        >
                          {note}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {error && <p className={styles["field-error"]}>{error}</p>}
        </div>
      );
    }

    if (kind === "richtext") {
      return (
        <div
          key={key}
          className={`${styles.field} ${styles["span-full"]} ${
            requiredField ? styles.required : ""
          }`}
        >
          <label htmlFor={`field-${key}`}>{toTitleCase(key)}</label>
          <p className={styles["field-hint"]}>
            For gradient theme text, use the editor&apos;s{" "}
            <strong>Background Color</strong> control (not text color).
          </p>
          <Editor
            key={`editor-${values?._id || values?._localId || "new"}-${key}`}
            id={`field-${key}`}
            defaultValue={value || ""}
            onChange={(content, _delta, source) => {
              if (source !== "user") return;
              onChange(key, content);
            }}
          />
          {error && <p className={styles["field-error"]}>{error}</p>}
        </div>
      );
    }

    if (kind === "date") {
      let dateVal = "";
      if (value) {
        try {
          const d = new Date(value);
          if (!Number.isNaN(d.getTime())) {
            dateVal = d.toISOString().slice(0, 10);
          }
        } catch {
          dateVal = "";
        }
      }
      const readOnly =
        mode === "edit" && (key === "createdAt" || key === "updatedAt");
      const isReleaseField = key === "releaseDate" || key === "realeaseDate";
      // Use live form values so changing the date updates upcoming status immediately
      const releaseProbe = isReleaseField
        ? { ...values, [key]: value }
        : null;
      const upcoming = releaseProbe ? isUpcomingRelease(releaseProbe) : false;
      const dropLabel = upcoming ? formatReleaseFullDate(releaseProbe) : "";
      const countdownLabel = upcoming
        ? formatReleaseCountdown(releaseProbe)
        : "";
      return (
        <div
          key={key}
          className={`${styles.field} ${requiredField ? styles.required : ""}`}
        >
          <label htmlFor={`field-${key}`}>
            {isReleaseField ? "Release date" : toTitleCase(key)}
          </label>
          {isReleaseField && (
            <p className={styles["field-hint"]}>
              If this date is after today, the public site shows the upcoming
              release teaser instead of the music player.
            </p>
          )}
          <input
            id={`field-${key}`}
            type="date"
            className={styles["text-input"]}
            value={dateVal}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={(e) => onChange(key, e.target.value)}
          />
          {isReleaseField && upcoming && (
            <div className={styles["upcoming-status"]} role="status">
              <p className={styles["upcoming-status-label"]}>
                Upcoming release
              </p>
              <p className={styles["upcoming-status-detail"]}>
                {dropLabel ? `Drops ${dropLabel}` : "Future release date set"}
                {countdownLabel ? ` · ${countdownLabel}` : ""}
              </p>
            </div>
          )}
          {isReleaseField && value && !upcoming && (
            <p className={styles["field-hint"]} style={{ marginTop: "0.35rem" }}>
              Not upcoming — release date is today or in the past (player will
              show when active).
            </p>
          )}
          {error && <p className={styles["field-error"]}>{error}</p>}
        </div>
      );
    }

    if (kind === "url") {
      return (
        <div
          key={key}
          className={`${styles.field} ${requiredField ? styles.required : ""}`}
        >
          <label htmlFor={`field-${key}`}>{toTitleCase(key)}</label>
          <input
            id={`field-${key}`}
            type="url"
            className={styles["text-input"]}
            value={value || ""}
            placeholder="https://"
            onChange={(e) => onChange(key, e.target.value)}
          />
          {error && <p className={styles["field-error"]}>{error}</p>}
        </div>
      );
    }

    // text / default — use textarea for longer-looking fields
    const useTextarea =
      key === "menuOrder" ||
      key === "tags" ||
      key === "preReleaseMessage" ||
      (typeof value === "string" && value.length > 80);

    return (
      <div
        key={key}
        className={`${styles.field} ${
          useTextarea ? styles["span-full"] : ""
        } ${requiredField ? styles.required : ""}`}
      >
        <label htmlFor={`field-${key}`}>
          {key === "preReleaseMessage"
            ? "Pre-release message"
            : toTitleCase(key)}
        </label>
        {key === "menuOrder" && (
          <p className={styles["field-hint"]}>
            Comma-separated nav keys, e.g.{" "}
            <code>noticeOne, noticeThree, about, noticeTwo, music</code>
          </p>
        )}
        {key === "title" && extraForm && (
          <p className={styles["field-hint"]}>
            Admin label only. Public copy is in the groups below.
          </p>
        )}
        {key === "preReleaseMessage" && (
          <p className={styles["field-hint"]}>
            Shown on the public site when this item is active but its release
            date is still in the future. Leave blank to use the default hype
            line (the site always shows <em>some</em> message for upcoming
            releases).
          </p>
        )}
        {key === "preReleaseMessage" && isUpcomingRelease(values) && (
          <div className={styles["upcoming-status"]} role="status">
            <p className={styles["upcoming-status-label"]}>
              Applies now — item is upcoming
            </p>
            <p className={styles["upcoming-status-detail"]}>
              {formatReleaseFullDate(values)
                ? `Drops ${formatReleaseFullDate(values)}`
                : "Future release date set"}
              {formatReleaseCountdown(values)
                ? ` · ${formatReleaseCountdown(values)}`
                : ""}
            </p>
          </div>
        )}
        {useTextarea ? (
          <textarea
            id={`field-${key}`}
            className={styles["textarea-input"]}
            value={value == null ? "" : String(value)}
            onChange={(e) => onChange(key, e.target.value)}
            rows={key === "preReleaseMessage" ? 3 : 3}
            placeholder={
              key === "preReleaseMessage"
                ? DEFAULT_PRE_RELEASE_MESSAGE
                : undefined
            }
          />
        ) : (
          <input
            id={`field-${key}`}
            type="text"
            className={styles["text-input"]}
            value={value == null ? "" : String(value)}
            onChange={(e) => onChange(key, e.target.value)}
          />
        )}
        {key === "preReleaseMessage" && (
          <p className={styles["field-hint"]} style={{ marginTop: "0.35rem" }}>
            <strong>Public preview:</strong>{" "}
            {getEffectivePreReleaseMessage(value)}
            {!String(value || "").trim() ? " (default — field is blank)" : ""}
          </p>
        )}
        {error && <p className={styles["field-error"]}>{error}</p>}
      </div>
    );
  };

  const renderExtraCmsField = (key, form) => {
    const meta = form.meta[key] || {};
    const label = meta.label || toTitleCase(key);
    const hint = meta.hint;
    const requiredField = required.includes(key);
    const value = values?.[key];
    const error = errors?.[key];
    const fallback = form.defaults[key];
    const placeholder =
      fallback == null || typeof fallback === "boolean"
        ? undefined
        : String(fallback);
    const numberSpec = form.numberFields?.[key];

    if (form.booleans.includes(key)) {
      return (
        <div
          key={key}
          className={`${styles.field} ${requiredField ? styles.required : ""}`}
        >
          <label className={styles["checkbox-row"]} htmlFor={`field-${key}`}>
            <input
              id={`field-${key}`}
              type="checkbox"
              checked={isTruthyFlag(value)}
              onChange={(e) =>
                onChange(key, e.target.checked ? "true" : "false")
              }
            />
            <span>{label}</span>
          </label>
          {hint && <p className={styles["field-hint"]}>{hint}</p>}
          {error && <p className={styles["field-error"]}>{error}</p>}
        </div>
      );
    }

    if (numberSpec) {
      return (
        <div
          key={key}
          className={`${styles.field} ${requiredField ? styles.required : ""}`}
        >
          <label htmlFor={`field-${key}`}>{label}</label>
          {hint && <p className={styles["field-hint"]}>{hint}</p>}
          <input
            id={`field-${key}`}
            type="number"
            min={numberSpec.min}
            max={numberSpec.max}
            step={numberSpec.step || 1}
            className={styles["text-input"]}
            value={value == null ? "" : String(value)}
            placeholder={placeholder}
            onChange={(e) => onChange(key, e.target.value)}
          />
          {error && <p className={styles["field-error"]}>{error}</p>}
        </div>
      );
    }

    const useTextarea = Boolean(meta.textarea);
    const inputType = (form.urlFields || []).includes(key) ? "url" : "text";
    return (
      <div
        key={key}
        className={`${styles.field} ${useTextarea ? styles["span-full"] : ""} ${
          requiredField ? styles.required : ""
        }`}
      >
        <label htmlFor={`field-${key}`}>{label}</label>
        {hint && <p className={styles["field-hint"]}>{hint}</p>}
        {useTextarea ? (
          <textarea
            id={`field-${key}`}
            className={styles["textarea-input"]}
            value={value == null ? "" : String(value)}
            placeholder={placeholder}
            onChange={(e) => onChange(key, e.target.value)}
            rows={3}
          />
        ) : (
          <input
            id={`field-${key}`}
            type={inputType}
            className={styles["text-input"]}
            value={value == null ? "" : String(value)}
            placeholder={
              inputType === "url" && !placeholder ? "https://" : placeholder
            }
            onChange={(e) => onChange(key, e.target.value)}
          />
        )}
        {error && <p className={styles["field-error"]}>{error}</p>}
      </div>
    );
  };

  if (!model) {
    return (
      <div className={styles["empty-state"]}>
        <h3>Schema unavailable</h3>
        <p>Could not load the field model from the server.</p>
      </div>
    );
  }

  return (
    <div>
      <div className={`${styles["form-grid"]} ${styles["two-col"]}`}>
        {genericMainFields.map(renderField)}
        {extraFields.map(renderField)}
      </div>

      {extraForm &&
        extraForm.groups.map((group) => (
          <div
            key={group.id}
            className={`${styles["connect-form-group"]} ${styles["item-type-surface"]} ${styles[`item-type-${extraForm.theme}`]}`}
          >
            <h3 className={styles["connect-form-group-title"]}>{group.title}</h3>
            <p className={styles["connect-form-group-hint"]}>{group.hint}</p>
            <div className={`${styles["form-grid"]} ${styles["two-col"]}`}>
              {extraForm.keys
                .filter((k) => extraForm.meta[k]?.group === group.id)
                .map((k) => renderExtraCmsField(k, extraForm))}
            </div>
          </div>
        ))}

      {(systemFields.length > 0 || onToggleSystem) && (
        <div className={styles["system-section"]}>
          {onToggleSystem && (
            <button
              type="button"
              className={styles["system-toggle"]}
              onClick={onToggleSystem}
            >
              {showSystem ? "Hide system fields" : "Show system fields"}
            </button>
          )}
          {showSystem && (
            <div className={`${styles["form-grid"]} ${styles["two-col"]}`}>
              {systemFields.map(renderField)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminFieldForm;
